import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { validateInputAction } from "../../actions/advisor";
import { backendUrl, country, termsOfAdvisoryPlan } from "../../constants";
import {
  addCampaignAction,
  addCampaignAiAction,
  getAIObjectivesAction,
  getAiAction,
  getAiFilterAction,
  getLeadsProfileAction,
  getMergedGoalsAction,
  setObjectiveFilter,
  updateObjectiveTitle,
} from "../../actions/peopleai";
import { RoutesPaths } from "../../Router/constant";
import {
  completePartnerActivationAction,
  getPartnerActivationStatusAction,
  verifyPartnerActivationAction,
} from "../../actions/auth";
import { adminChatMessage } from "../../actions/chatv2";
import { editCompany, getAllCompany } from "../../actions/company";
import { singleFileUploadAction } from "../../actions/files";
import {
  PARTNER_AUDIENCE_OPTIONS,
  PARTNER_DEMO_USER,
  PARTNER_GOAL_INFO,
  PARTNER_GOAL_QUESTION,
  PARTNER_SKILL_OPTIONS,
  PARTNER_SUCCESS,
  PARTNER_STEP_INTRO,
  PARTNER_STEPS,
} from "./activatePartnerStaticData";
import { SKILLS } from "./partnerSkills";
import VerificationIcons from "../../components/VerificationIcons";
// Reuse the activate-business styles so both flows look the same.
import "../activateBusiness/ActivateBusiness.css";
import "./partnerTheme.css";
import "./ActivatePartner.css";

const TOTAL_STEPS = PARTNER_STEPS.length;

// Step numbers, in the order of PARTNER_STEPS.
const STEP = {
  PROFILE: 1,
  GOALS: 2,
  PREVIEW: 3,
};

const COUNTRY_OPTIONS = country.map(item => ({ code: item.code, title: item.name }));
const LINKEDIN_PREFIX = "https://www.linkedin.com/in/";

const normalizeLinkedInUrl = (value = "") => {
  if (!value) return "";
  if (value.startsWith(LINKEDIN_PREFIX)) return value;

  const username = value.replace(/https?:\/\/(www\.)?linkedin\.com\/in\//i, "");
  return `${LINKEDIN_PREFIX}${username}`;
};

const getAssetUrl = value => {
  if (!value) return "";
  if (/^(blob:|data:|https?:\/\/)/i.test(value)) return value;
  return backendUrl ? `${backendUrl}${value}` : value;
};

const getDateInputValue = value => (value ? String(value).slice(0, 10) : "");
const getOptionalText = value => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};
const PARTNER_COMPANY_TYPE = 0;

const getUniqueUser = () => {
  try {
    return JSON.parse(localStorage.getItem("uniqueUser")) || null;
  } catch {
    return null;
  }
};

const splitFilterValues = value => {
  if (Array.isArray(value)) return value.map(item => String(item || "").trim()).filter(Boolean);
  return String(value || "").split(",").map(item => item.trim()).filter(Boolean);
};

const filterValuesToQuery = value => encodeURIComponent(splitFilterValues(value).join(","));

const filterValuesToLabel = value => splitFilterValues(value).join(", ");

const getInitials = (name = "") => {
  const parts = name.trim().split(" ").filter(Boolean);
  if (!parts.length) return "RS";
  return parts.slice(0, 2).map(part => part[0]).join("").toUpperCase();
};

function StepRail({ selectedStep }) {
  return (
    <aside className="activate-sidebar">
      <img
        src="assets/img/inproved-logo.svg"
        className="inproved-logo cursorPointer mb-4"
        alt="kimbocorp"
      />
      <div className="activate-step-list">
        {PARTNER_STEPS.map((label, index) => {
          const step = index + 1;
          const isComplete = step < selectedStep;
          const isActive = step === selectedStep;

          return (
            <div className={`activate-step ${isActive ? "active" : ""} ${isComplete ? "complete" : ""}`} key={label}>
              <div className="activate-step-marker">
                {isComplete ? <span className="activate-step-check" aria-hidden="true" /> : step}
              </div>
              <div>
                <p>{label}</p>
                <span>{isComplete ? "Completed" : isActive ? "In progress" : "Not started"}</span>
              </div>
            </div>
          );
        })}
      </div>
    </aside>
  );
}

function ActivationHeader({ user }) {
  const name = `${user?.first_name || PARTNER_DEMO_USER.first_name} ${user?.last_name || PARTNER_DEMO_USER.last_name}`.trim();
  const email = user?.email || PARTNER_DEMO_USER.email;

  return (
    <header className="activate-header">
      <div className="activate-heading">
        <h1>Activate your partner account</h1>
      </div>
      <div className="activate-user">
        <div className="activate-avatar">{getInitials(name)}</div>
        <div>
          <strong>{name}</strong>
          <p>{email}</p>
        </div>
      </div>
    </header>
  );
}

function StepIntroCard({ step, subStep, title, subtitle }) {
  const content = PARTNER_STEP_INTRO[step];

  if (!content) return null;

  const stepLabel = subStep ? `Step ${step}.${subStep}` : `Step ${step}`;
  const displayTitle = title ?? content.title;
  const displaySubtitle = subtitle ?? content.subtitle;

  return (
    <div className="activate-substep-card activate-step-card">
      <div>
        <p className="activate-step-card-title"><strong>{stepLabel}: </strong> {displayTitle}</p>
        {displaySubtitle && <span className="activate-step-card-subtitle">{displaySubtitle}</span>}
      </div>
    </div>
  );
}

function OptionDropdown({ name, label, placeholder, options, value, register, setValue, requiredMessage, error }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const dropdownRef = useRef(null);
  const listId = `${name}Options`;
  const selectedOption = options.find(option => option.title === value);
  const registration = register(name, requiredMessage ? { required: requiredMessage } : undefined);
  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return options;
    return options.filter(option => option.title.toLowerCase().includes(normalizedQuery));
  }, [options, query]);

  useEffect(() => {
    if (!open) return undefined;

    const handleOutsideClick = event => {
      if (!dropdownRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = event => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const handleSelect = nextValue => {
    setValue(name, nextValue, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
    setQuery("");
    setOpen(false);
  };

  return (
    <div className="activate-field activate-dropdown-field" ref={dropdownRef}>
      <span id={`${name}Label`}>{label}</span>
      <input type="hidden" {...registration} value={value || ""} readOnly />
      <button
        type="button"
        className={`activate-activity-select ${open ? "open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={`${name}Label`}
        aria-controls={listId}
        onClick={() => setOpen(isOpen => !isOpen)}
      >
        <span>{selectedOption?.title || placeholder}</span>
      </button>
      {open && (
        <div className="activate-activity-menu">
          <input
            className="activate-activity-search"
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="Search"
            autoFocus
          />
          <div className="activate-activity-options" id={listId} role="listbox" aria-labelledby={`${name}Label`}>
            {filteredOptions.map(option => (
              <button
                type="button"
                className={option.title === value ? "selected" : ""}
                key={option.code}
                role="option"
                aria-selected={option.title === value}
                onClick={() => handleSelect(option.title)}
              >
                {option.title}
              </button>
            ))}
            {filteredOptions.length === 0 && (
              <div className="activate-activity-empty">No options found</div>
            )}
          </div>
        </div>
      )}
      {error && <small>{error.message}</small>}
    </div>
  );
}

function PartnerInfoStep({ register, errors, watch, setValue }) {
  const dispatch = useDispatch();
  const linkedin = watch("linkedin") || "";
  const photo = watch("photo");
  const about = watch("about") || "";
  const selectedCountry = watch("country") || "";
  const relatedCompanyName = watch("releatedCompanyName") || "";
  const watchedSkills = watch("skills");
  const skills = useMemo(() => watchedSkills || [], [watchedSkills]);
  const photoInputRef = useRef(null);
  const generatedAboutSkillsRef = useRef("");
  const [skillInput, setSkillInput] = useState("");
  const [skillValidationError, setSkillValidationError] = useState("");
  const [skillValidationLoading, setSkillValidationLoading] = useState(false);
  const [aboutGenerating, setAboutGenerating] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const skillOptions = useMemo(
    () => Array.from(new Set([...SKILLS, ...PARTNER_SKILL_OPTIONS, ...skills])),
    [skills]
  );
  const skillSuggestions = useMemo(() => {
    const query = skillInput.trim().toLowerCase();
    if (!query) return [];

    return skillOptions
      .filter(option => !skills.some(skill => skill.toLowerCase() === option.toLowerCase()))
      .filter(option => option.toLowerCase().includes(query))
      .slice(0, 8);
  }, [skillInput, skillOptions, skills]);

  // Skills are managed as an array, matching the React Native form.
  useEffect(() => {
    register("skills");
  }, [register]);

  // Generate the advisor description whenever the selected skill list changes.
  useEffect(() => {
    const skillKey = skills.join("|");
    if (!skillKey) {
      generatedAboutSkillsRef.current = "";
      return undefined;
    }
    if (generatedAboutSkillsRef.current === skillKey) return undefined;

    generatedAboutSkillsRef.current = skillKey;
    let isActive = true;
    setAboutGenerating(true);

    const query = skills
      .map(skill => `skills[]=${encodeURIComponent(skill)}`)
      .join("&");

    dispatch(validateInputAction(`?path=api/advisor-about&${query}`))
      .then(response => {
        const generatedAbout = [
          typeof response === "string" ? response : "",
          response?.data?.about,
          typeof response?.data === "string" ? response.data : "",
          response?.about,
          response?.text,
        ].find(value => typeof value === "string" && value.trim());

        if (isActive && generatedAbout) {
          setValue("about", generatedAbout.trim(), { shouldDirty: true, shouldValidate: true });
        }
      })
      .catch(() => { })
      .finally(() => {
        if (isActive) setAboutGenerating(false);
      });

    return () => {
      isActive = false;
    };
  }, [dispatch, setValue, skills]);

  const setSkills = nextSkills =>
    setValue("skills", nextSkills, { shouldDirty: true, shouldValidate: true });

  const selectSkill = skill => {
    const normalizedSkill = skill.trim();
    if (!normalizedSkill) return;

    const alreadySelected = skills.some(item => item.toLowerCase() === normalizedSkill.toLowerCase());
    if (!alreadySelected) setSkills([...skills, normalizedSkill]);
    setSkillInput("");
    setSkillValidationError("");
  };

  const toggleSkill = skill =>
    setSkills(skills.some(item => item.toLowerCase() === skill.toLowerCase())
      ? skills.filter(item => item.toLowerCase() !== skill.toLowerCase())
      : [...skills, skill]);

  const addSkill = async () => {
    const value = skillInput.trim();
    if (!value || skillValidationLoading) return;

    const existing = skillOptions.find(item => item.toLowerCase() === value.toLowerCase());
    if (existing) {
      selectSkill(existing);
      return;
    }

    setSkillValidationError("");
    setSkillValidationLoading(true);
    try {
      const response = await dispatch(
        validateInputAction(`?path=api/validate-skills&skills=${encodeURIComponent(value)}`)
      );
      const row = Array.isArray(response?.data) ? response.data[0] : null;
      const validatedSkill = String(row?.skill || value).trim();

      if (row?.valid !== true || !validatedSkill) {
        setSkillValidationError("This skill could not be validated. Choose a suggested skill or try another one.");
        return;
      }

      selectSkill(validatedSkill);
    } catch (error) {
      setSkillValidationError("This skill could not be validated. Please try again.");
    } finally {
      setSkillValidationLoading(false);
    }
  };

  const handlePhotoChange = event => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    setPhotoUploading(true);
    dispatch(singleFileUploadAction(formData))
      .then(filename => {
        if (filename) setValue("photo", filename, { shouldDirty: true });
      })
      .finally(() => setPhotoUploading(false));
    event.target.value = null;
  };

  return (
    <>
      <StepIntroCard step={STEP.PROFILE} />
      <div className="activate-form-grid">
        <label className="activate-field activate-wide">
          <span>Name*</span>
          <input maxLength={100} placeholder="Enter name" {...register("name", { required: "Name is required" })} />
          {errors.name && <small>{errors.name.message}</small>}
        </label>
        {relatedCompanyName.trim() && (
          <label className="activate-field activate-wide">
            <span>Company or practice name</span>
            <input maxLength={100} placeholder="Company or practice name" {...register("releatedCompanyName")} />
            <small className="partner-field-help">Leave blank if you work independently.</small>
          </label>
        )}
        <label className="activate-field activate-wide">
          <span>Email*</span>
          <input readOnly className="activate-readonly-field" {...register("email", { required: "Email is required" })} />
          {errors.email && <small>{errors.email.message}</small>}
        </label>
        <OptionDropdown
          name="country"
          label="Where you're based*"
          placeholder="Select country"
          options={COUNTRY_OPTIONS}
          value={selectedCountry}
          register={register}
          setValue={setValue}
          requiredMessage="Country is required"
          error={errors.country}
        />
        <label className="activate-field activate-wide">
          <span>LinkedIn URL</span>
          <input
            value={linkedin}
            placeholder="https://www.linkedin.com/in/your-profile"
            {...register("linkedin")}
            onChange={event => setValue("linkedin", normalizeLinkedInUrl(event.target.value), { shouldDirty: true })}
          />
          <small className="partner-field-help">Adds a verified badge to your profile.</small>
        </label>
        <div className="partner-photo-field activate-wide">
          <div className="partner-photo">
            {photo ? <img src={getAssetUrl(photo)} alt="Profile" /> : <span className="partner-photo-placeholder" aria-hidden="true" />}
          </div>
          <div className="partner-photo-actions">
            <span>Profile photo</span>
            <div>
              <button type="button" disabled={photoUploading} onClick={() => photoInputRef.current?.click()}>
                {photoUploading ? "Uploading..." : photo ? "Replace" : "Upload photo"}
              </button>
              {photo && <button type="button" onClick={() => setValue("photo", "")}>Remove</button>}
            </div>
          </div>
          <input ref={photoInputRef} type="file" accept="image/*" onChange={handlePhotoChange} hidden />
        </div>
        <div className="activate-wide partner-skills-field">
          <span className="partner-field-label">Areas of expertise</span>
          <div className="partner-skill-input-row">
            <input
              value={skillInput}
              onChange={event => {
                setSkillInput(event.target.value);
                setSkillValidationError("");
              }}
              onKeyDown={event => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  addSkill();
                }
              }}
              maxLength={25}
              placeholder="e.g. corporate tax, trade finance, wealth planning"
              aria-label="Areas of expertise"
            />
            <button type="button" onClick={addSkill} disabled={!skillInput.trim() || skillValidationLoading}>
              {skillValidationLoading ? "Checking..." : "Add"}
            </button>
          </div>
          {skillInput.trim() && (
            <div className="partner-skill-suggestions" role="listbox" aria-label="Skill suggestions">
              {skillSuggestions.map(skill => (
                <button type="button" key={skill} onClick={() => selectSkill(skill)} role="option" aria-selected="false">
                  {skill}
                </button>
              ))}
              {!skillSuggestions.length && !skillOptions.some(option => option.toLowerCase() === skillInput.trim().toLowerCase()) && (
                <p>No matching skill. Use Add to validate this custom skill.</p>
              )}
            </div>
          )}
          <ChipGroup options={skills} selected={skills} onToggle={toggleSkill} />
          {skillValidationError && <small className="partner-field-error">{skillValidationError}</small>}
          {aboutGenerating && <small className="partner-field-help">Generating your advisor description…</small>}
        </div>
        <label className="activate-field activate-wide">
          <span>About you*</span>
          <textarea
            maxLength={1000}
            placeholder="Chartered accountant in Mumbai. I advise exporters and family businesses, and often meet clients who need a Singapore company."
            disabled={aboutGenerating}
            {...register("about", {
              required: "About you is required",
            })}
          />
          <em>{about.trim().length} / 1000</em>
          <small className="partner-field-help">Two or three sentences: what you do, who you work with, and where. Clients read this first.</small>
          {errors.about && <small>{errors.about.message}</small>}
        </label>
      </div>
    </>
  );
}

function SummaryTile({ title, value, description, onClick }) {
  const Tag = onClick ? "button" : "div";

  return (
    <Tag type={onClick ? "button" : undefined} className={`activate-summary-tile ${onClick ? "clickable" : ""}`} onClick={onClick}>
      <span className="activate-summary-check" aria-hidden="true" />
      <div>
        <strong>{title}</strong>
        <p>{value}</p>
        {description && <em>{description}</em>}
      </div>
    </Tag>
  );
}

function ChipGroup({ options, selected, onToggle }) {
  return (
    <div className="activate-chip-group">
      {options.map(option => {
        const isSelected = selected.includes(option);
        return (
          <button
            type="button"
            className={isSelected ? "selected" : ""}
            onClick={() => onToggle(option)}
            key={option}
          >
            {option}
            {isSelected && <span>x</span>}
          </button>
        );
      })}
    </div>
  );
}

function AudienceGroup({ title, options, selected, onToggle, onAdd }) {
  const [isAdding, setIsAdding] = useState(false);
  const [newValue, setNewValue] = useState("");

  const handleCancel = () => {
    setNewValue("");
    setIsAdding(false);
  };

  const handleSave = () => {
    if (!newValue.trim()) return;
    if (!onAdd(newValue)) return;
    handleCancel();
  };

  return (
    <div className="activate-audience-group">
      <h4>{title}</h4>
      <ChipGroup options={options} selected={selected} onToggle={onToggle} />
      {isAdding ? (
        <div className="activate-audience-add-row">
          <input
            value={newValue}
            onChange={event => setNewValue(event.target.value)}
            onKeyDown={event => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleSave();
              }
            }}
            placeholder={`Add ${title.toLowerCase()}...`}
            autoFocus
          />
          <button type="button" onClick={handleCancel}>Cancel</button>
          <button type="button" onClick={handleSave} disabled={!newValue.trim()}>Add</button>
        </div>
      ) : (
        <button type="button" className="activate-add-small" onClick={() => setIsAdding(true)}>+ Add</button>
      )}
    </div>
  );
}

function GoalsObjectivesStep({ advisor, companyId, initialData, onBack, onContinue }) {
  const dispatch = useDispatch();
  const { goalsList, goalsListLoader } = useSelector(state => state.peopleai);
  const [subStep, setSubStep] = useState(1);
  const [selectedGoalId, setSelectedGoalId] = useState(initialData?.goal?.id || initialData?.goal?.key || null);
  const [chips, setChips] = useState([]);
  const [selectedObjectives, setSelectedObjectives] = useState(initialData?.objectives || []);
  const [customObjective, setCustomObjective] = useState("");
  const [showCustomObjectiveInput, setShowCustomObjectiveInput] = useState(false);
  const [statement, setStatement] = useState(initialData?.statement || { title: "", body: "" });
  const [statementDraft, setStatementDraft] = useState(initialData?.statement || { title: "", body: "" });
  const [isStatementEditing, setIsStatementEditing] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    role: [],
    location: [],
    industry: [],
  });
  const [audience, setAudience] = useState(initialData?.audience || {
    role: [],
    location: [],
    industry: [],
  });
  const [verifiedOnly] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [goalsLoadError, setGoalsLoadError] = useState(false);
  const [goalsRetryCount, setGoalsRetryCount] = useState(0);
  const goalsFetchKeyRef = useRef("");
  const companyDescription = advisor?.about || "";
  const goalQuestion = PARTNER_GOAL_QUESTION;
  const filterGroups = [
    { key: "role", title: "Their role" },
    { key: "location", title: "Where they are" },
    { key: "industry", title: "Their industry" },
  ];

  const allGoalOptions = useMemo(() => {
    return Array.isArray(goalsList) ? goalsList : [];
  }, [goalsList]);

  const selectedGoal = allGoalOptions.find(goal => String(goal.id) === String(selectedGoalId)) || initialData?.goal || null;
  const chipsQuery = selectedObjectives.map(chip => `chips[]=${encodeURIComponent(chip)}`).join("&");
  const isLoading = Boolean(loadingLabel) || (subStep === 1 && goalsListLoader);

  useEffect(() => {
    if (!advisor?.companyName) return undefined;

    const skillsQuery = (advisor.skills || [])
      .map(skill => `skills[]=${encodeURIComponent(skill)}`)
      .join("&");
    const fetchKey = JSON.stringify({
      companyId,
      companyName: advisor.companyName,
      about: companyDescription,
      country: advisor.country || "",
      skills: advisor.skills || [],
    });
    if (goalsFetchKeyRef.current === fetchKey) return undefined;
    goalsFetchKeyRef.current = fetchKey;
    setGoalsLoadError(false);

    const query = `?path=api/generate-goal&companyName=${encodeURIComponent(advisor.companyName)}&type=${PARTNER_COMPANY_TYPE}&companyDescription=${encodeURIComponent(companyDescription)}${skillsQuery ? `&${skillsQuery}` : ""}&country=${encodeURIComponent(advisor.country || "")}`;
    let isActive = true;

    dispatch(getMergedGoalsAction("advisor", advisor, query))
      .then(response => {
        if (!isActive) return;
        const responseGoals = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.goals)
            ? response.data.goals
            : [];
        setGoalsLoadError(!response?.status || !responseGoals.length);
      })
      .catch(() => {
        if (isActive) setGoalsLoadError(true);
      });

    return () => {
      isActive = false;
    };
  }, [advisor, companyDescription, companyId, dispatch, goalsRetryCount]);

  const retryGoals = () => {
    goalsFetchKeyRef.current = "";
    setGoalsLoadError(false);
    setGoalsRetryCount(count => count + 1);
  };

  const setActionError = message => {
    setLoadingLabel("");
    setErrorMessage(message);
  };

  const handleGoalSelect = goal => {
    setSelectedGoalId(goal.id);
    setErrorMessage("");
    dispatch(setObjectiveFilter(goal));
  };

  const toggleObjective = objective => {
    setSelectedObjectives(current => {
      if (current.includes(objective)) {
        return current.length === 1 ? current : current.filter(item => item !== objective);
      }
      return current.length < 8 ? [...current, objective] : current;
    });
  };

  const addCustomObjective = () => {
    const value = customObjective.trim();
    if (!value || selectedObjectives.length >= 8) return;
    const existing = chips.find(chip => chip.toLowerCase() === value.toLowerCase());
    const option = existing || value;
    if (!selectedObjectives.some(item => item.toLowerCase() === option.toLowerCase())) {
      setChips(current => existing ? current : [value, ...current]);
      setSelectedObjectives(current => [...current, option]);
    }
    setCustomObjective("");
    setShowCustomObjectiveInput(false);
  };

  const toggleAudience = (group, value) => {
    setAudience(current => {
      const selected = current[group] || [];
      const isSelected = selected.includes(value);
      return {
        ...current,
        [group]: isSelected
          ? selected.filter(item => item !== value)
          : selected.length < 3 || group === "location"
            ? [...selected, value]
            : selected,
      };
    });
  };

  const addAudienceOption = (group, rawValue) => {
    const value = rawValue.trim();
    if (!value) return false;
    setFilterOptions(current => ({
      ...current,
      [group]: current[group].some(item => item.toLowerCase() === value.toLowerCase())
        ? current[group]
        : [...current[group], value],
    }));
    setAudience(current => ({
      ...current,
      [group]: current[group].some(item => item.toLowerCase() === value.toLowerCase())
        ? current[group]
        : [...current[group], value],
    }));
    return true;
  };

  const generateChips = async () => {
    if (!selectedGoal) {
      setActionError("Please select a goal to continue.");
      return;
    }
    setErrorMessage("");
    setLoadingLabel("Crafting objectives for you based on your selected goal");
    try {
      const query = `?path=api/generate-chips&title=${encodeURIComponent(selectedGoal.name)}&description=${encodeURIComponent(selectedGoal.description || "")}&companyDescription=${encodeURIComponent(companyDescription)}&type=${PARTNER_COMPANY_TYPE}`;
      const response = await dispatch(getAiAction(query));
      const nextChips = Array.isArray(response?.data?.chips) ? response.data.chips : [];
      if (!response?.status || !nextChips.length) {
        setActionError("We could not generate objectives for this goal. Please try again.");
        return;
      }
      setChips(nextChips);
      setSelectedObjectives(nextChips.slice(0, 5));
      setSubStep(2);
    } catch (error) {
      setActionError("We could not generate objectives for this goal. Please try again.");
    } finally {
      setLoadingLabel("");
    }
  };

  const generateStatement = async () => {
    if (selectedObjectives.length < 3) {
      setActionError("Please select at least 3 objectives.");
      return;
    }
    setErrorMessage("");
    setLoadingLabel("Generating your objective statement");
    try {
      const query = `?path=api/generate-objectives&title=${encodeURIComponent(selectedGoal?.name || "")}&description=${encodeURIComponent(selectedGoal?.description || "")}&${chipsQuery}&companyDescription=${encodeURIComponent(companyDescription)}&type=${PARTNER_COMPANY_TYPE}`;
      const response = await dispatch(getAIObjectivesAction(query));
      const nextStatement = {
        title: response?.data?.title || "",
        body: response?.data?.description || response?.data?.body || "",
      };
      if (!response?.status || !nextStatement.title || !nextStatement.body) {
        setActionError("We could not generate the objective statement. Please try again.");
        return;
      }
      setStatement(nextStatement);
      setStatementDraft(nextStatement);
      setSubStep(3);
    } catch (error) {
      setActionError("We could not generate the objective statement. Please try again.");
    } finally {
      setLoadingLabel("");
    }
  };

  const generateFilters = async () => {
    if (!statement.title.trim() || !statement.body.trim()) {
      setActionError("Please add an objective title and description.");
      return;
    }
    setErrorMessage("");
    setLoadingLabel("Crafting audience for you based on your objective");
    try {
      const query = `?path=api/generate-targeting&title=${encodeURIComponent(selectedGoal?.name || "")}&description=${encodeURIComponent(selectedGoal?.description || "")}&${chipsQuery}&objectiveTitle=${encodeURIComponent(statement.title)}&objectiveDescription=${encodeURIComponent(statement.body)}&type=${PARTNER_COMPANY_TYPE}`;
      const response = await dispatch(getAiFilterAction(query));
      const data = response?.data || {};
      const stepOneCountry = splitFilterValues(advisor.country);
      const locationOptions = Array.from(new Set([
        ...splitFilterValues(data.country || data.location),
        ...stepOneCountry,
        "Singapore",
        "India",
        "Malaysia",
        "Indonesia",
      ]));
      const nextOptions = {
        role: splitFilterValues(data.role || data.targetting || PARTNER_AUDIENCE_OPTIONS.role),
        location: locationOptions,
        industry: splitFilterValues(data.industry || PARTNER_AUDIENCE_OPTIONS.industry),
      };
      setFilterOptions(nextOptions);
      setAudience({
        role: [],
        location: [],
        industry: [],
      });
      if (!response?.status) {
        setActionError("We could not generate audience filters. Please try again.");
        return;
      }
      setSubStep(4);
    } catch (error) {
      setActionError("We could not generate audience filters. Please try again.");
    } finally {
      setLoadingLabel("");
    }
  };

  const buildCampaignPreview = async () => {
    const missingFilters = filterGroups.filter(group => !audience[group.key]?.length).map(group => group.title);
    if (missingFilters.length) {
      setActionError(`Please select: ${missingFilters.join(", ")}`);
      return;
    }

    setErrorMessage("");
    setLoadingLabel("Finding matching profiles and saving your campaign draft");
    const snapshot = {
      title: selectedGoal?.name || "",
      description: selectedGoal?.description || "",
      objectiveTitle: statement.title,
      objectiveDescription: statement.body,
      chips: selectedObjectives,
      isVerified: verifiedOnly,
      ...audience,
    };
    const locationQuery = splitFilterValues(snapshot.location).map(value => encodeURIComponent(value)).join(",");
    const query = `?path=api/search&title=${encodeURIComponent(snapshot.title)}&description=${encodeURIComponent(snapshot.description)}&objectiveTitle=${encodeURIComponent(snapshot.objectiveTitle)}&objectiveDescription=${encodeURIComponent(snapshot.objectiveDescription)}&${chipsQuery}${locationQuery ? `&country=${locationQuery}` : ""}&role=${filterValuesToQuery(snapshot.role)}&industry=${filterValuesToQuery(snapshot.industry)}&isverified=${verifiedOnly}&page=1&pageSize=10`;
    const payloadData = {
      title: snapshot.title,
      description: snapshot.description,
      location: filterValuesToLabel(snapshot.location),
      industry: filterValuesToLabel(snapshot.industry),
      role: filterValuesToLabel(snapshot.role),
      isVerified: verifiedOnly,
      objectiveTitle: snapshot.objectiveTitle,
      objectiveDescription: snapshot.objectiveDescription,
      chips: chipsQuery,
    };

    try {
      const response = await dispatch(getLeadsProfileAction(query, null, payloadData, null, true, true));
      if (!response?.status) {
        setActionError("We could not find matching profiles. Please try again.");
        return;
      }

      const campaignDetail = response?.data?.campaign || {};
      const campaign = {
        ...campaignDetail,
        company: companyId,
        location: snapshot.location,
        industry: snapshot.industry,
        targetting: snapshot.role,
        onlyVerifiedUsers: verifiedOnly,
        name: campaignDetail.name || snapshot.objectiveTitle,
        description: campaignDetail.description || snapshot.objectiveDescription,
        tags: campaignDetail.tags || selectedObjectives,
        profiles: campaignDetail.profiles || response?.data?.profiles || [],
        status: "draft",
      };
      const preview = {
        goal: selectedGoal,
        objectives: selectedObjectives,
        statement,
        audience,
        filterOptions,
        campaign,
        advisor,
      };
      dispatch(setObjectiveFilter({ ...selectedGoal, ...snapshot, campaign }));
      await dispatch(addCampaignAiAction({ campaign }));
      onContinue(preview);
    } catch (error) {
      setActionError("We could not prepare the advisor preview. Please try again.");
    } finally {
      setLoadingLabel("");
    }
  };

  const handlePrimaryAction = () => {
    if (isLoading) return;
    if (subStep === 1) return generateChips();
    if (subStep === 2) return generateStatement();
    if (subStep === 3) return generateFilters();
    return buildCampaignPreview();
  };

  const saveStatementEdit = event => {
    event.preventDefault();
    const nextStatement = {
      title: statementDraft.title.trim(),
      body: statementDraft.body.trim(),
    };
    if (!nextStatement.title || !nextStatement.body) return;
    setStatement(nextStatement);
    setStatementDraft(nextStatement);
    dispatch(updateObjectiveTitle({ title: nextStatement.title, description: nextStatement.body }));
    setIsStatementEditing(false);
  };

  const handleLocalBack = () => {
    setErrorMessage("");
    if (subStep === 1) return onBack();
    setSubStep(step => step - 1);
  };

  const actionLabel = subStep === 1
    ? "Proceed"
    : subStep === 2
      ? "Write my partner summary"
      : subStep === 3
        ? "Proceed"
        : "Preview my profile";

  return (
    <div className="activate-goals">
      {loadingLabel && <div className="activate-ai-loading">{loadingLabel}</div>}
      <StepIntroCard
        step={STEP.GOALS}
        subStep={subStep}
        title={subStep === 2 ? "Client needs" : subStep === 3 ? "Your partner summary" : subStep === 4 ? "Your clients" : undefined}
        subtitle={subStep === 2 || subStep === 3 || subStep === 4 ? "" : undefined}
      />

      {subStep === 1 && (
        <section className="activate-goal-section">
          <div className="activate-section-heading">
            <h3>{goalQuestion}</h3>
          </div>
          <div className="activate-dashed-note partner-goal-info">{PARTNER_GOAL_INFO}</div>
          {goalsListLoader && !goalsLoadError && <div className="activate-empty-state">Loading goals…</div>}
          {goalsLoadError ? (
            <div className="activate-empty-state activate-goal-load-error">
              <p>We couldn’t load the options.</p>
              <button type="button" onClick={retryGoals} disabled={goalsListLoader}>Try again</button>
            </div>
          ) : (
            <div className="activate-chip-group activate-goal-chip-group">
              {allGoalOptions.map(goal => {
                const goalId = goal.id || goal.key || goal.name;
                const isSelected = String(selectedGoalId) === String(goalId);
                return (
                  <button type="button" className={isSelected ? "selected" : ""} title={goal.description} onClick={() => handleGoalSelect({ ...goal, id: goalId })} key={goalId}>
                    {goal.name || goal.title}
                  </button>
                );
              })}
            </div>
          )}
        </section>
      )}

      {subStep === 2 && (
        <section className="activate-goal-section">
          <SummaryTile title={goalQuestion} value={selectedGoal?.name} onClick={() => setSubStep(1)} />
          <div className="activate-section-heading with-count">
            <h3>Which client needs do you come across?</h3>
            <span>{selectedObjectives.length}/{chips.length} selected</span>
          </div>
          <p className="activate-goal-copy mt-0">Pick at least 3. These decide which kimbocorp plans we show you and which clients we match you with.</p>
          <ChipGroup options={chips} selected={selectedObjectives} onToggle={toggleObjective} />
          {!showCustomObjectiveInput ? (
            <button type="button" className="activate-chip-add activate-objective-add" onClick={() => setShowCustomObjectiveInput(true)}>+ Other</button>
          ) : (
            <div className="activate-add-chip-row">
              <input value={customObjective} onChange={event => setCustomObjective(event.target.value)} placeholder="Add new" autoFocus />
              <button type="button" onClick={() => { setCustomObjective(""); setShowCustomObjectiveInput(false); }}>Cancel</button>
              <button type="button" onClick={addCustomObjective} disabled={!customObjective.trim()}>Save</button>
            </div>
          )}
        </section>
      )}

      {subStep === 3 && (
        <section className="activate-goal-section">
          <SummaryTile title={goalQuestion} value={selectedGoal?.name} onClick={() => setSubStep(1)} />
          <SummaryTile title="Client needs" value={selectedObjectives.join(", ")} onClick={() => setSubStep(2)} />
          <div className="activate-section-heading"><h3>Your partner summary</h3></div>
          <p className="activate-goal-copy mt-0">Written from your answers. Edit it so it sounds like you.</p>
          <div className={`activate-statement-card ${isStatementEditing ? "editing" : ""}`}>
            {isStatementEditing ? (
              <form className="activate-statement-form" onSubmit={saveStatementEdit}>
                <label className="activate-field"><span>Objective title</span><input value={statementDraft.title} onChange={event => setStatementDraft(prev => ({ ...prev, title: event.target.value }))} placeholder="Enter objective title" /></label>
                <label className="activate-field"><span>Objective description</span><textarea value={statementDraft.body} onChange={event => setStatementDraft(prev => ({ ...prev, body: event.target.value }))} placeholder="Enter objective description" /></label>
                <div className="activate-statement-actions"><button type="button" onClick={() => setIsStatementEditing(false)}>Cancel</button><button type="submit" disabled={!statementDraft.title.trim() || !statementDraft.body.trim()}>Save</button></div>
              </form>
            ) : (
              <>
                <button type="button" className="activate-statement-edit partner-statement-edit" onClick={() => { setStatementDraft(statement); setIsStatementEditing(true); }} aria-label="Edit objective statement" title="Edit">
                  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" /></svg>
                </button>
                <h3>{statement.title}</h3>
                <p>{statement.body}</p>
              </>
            )}
          </div>
        </section>
      )}

      {subStep === 4 && (
        <section className="activate-goal-section">
          <SummaryTile title={goalQuestion} value={selectedGoal?.name} onClick={() => setSubStep(1)} />
          <SummaryTile title="Client needs" value={statement.title} description={statement.body} onClick={() => setSubStep(3)} />
          <div className="activate-section-heading"><h3>Who are your clients?</h3></div>
          <p className="activate-goal-copy mt-0">We use this to match you with businesses on kimbocorp.</p>
          {filterGroups.map(group => (
            <AudienceGroup key={group.key} title={group.title} options={filterOptions[group.key]} selected={audience[group.key]} onToggle={value => toggleAudience(group.key, value)} onAdd={value => addAudienceOption(group.key, value)} />
          ))}
          {/* <label className="activate-checkbox-field"><input type="checkbox" checked={verifiedOnly} onChange={event => setVerifiedOnly(event.target.checked)} /><span>Only show verified profiles</span></label> */}
        </section>
      )}

      {errorMessage && <div className="activate-form-error">{errorMessage}</div>}
      <div className="activate-actions activate-goal-actions">
        <button type="button" className="activate-back" onClick={handleLocalBack} disabled={isLoading}>Back</button>
        <button type="button" className="activate-continue" onClick={handlePrimaryAction} disabled={isLoading}>{isLoading && <span className="activate-button-loader" aria-hidden="true" />}{actionLabel}</button>
      </div>
    </div>
  );
}

function AdvisorPreviewStep({ partnerData, campaignData }) {
  const uniqueUser = getUniqueUser();
  const advisor = campaignData?.advisor || {};
  const memberSince = advisor.createdAt || advisor.created_at;
  const memberSinceLabel = memberSince
    ? new Date(memberSince).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "";
  const advisorName = getOptionalText(partnerData.name) || getOptionalText(advisor.companyName) || "Advisor";
  const releatedCompanyName = getOptionalText(partnerData.releatedCompanyName) || getOptionalText(advisor.releatedCompanyName);
  const advisorLocation = getOptionalText(partnerData.country) || getOptionalText(advisor.country?.name || advisor.country);
  const advisorEmail = getOptionalText(partnerData.email) || getOptionalText(advisor.email);
  const advisorLinkedIn = getOptionalText(partnerData.linkedin);
  const advisorAbout = getOptionalText(partnerData.about) || getOptionalText(advisor.about);
  const advisorSkills = Array.isArray(partnerData.skills) && partnerData.skills.length
    ? partnerData.skills
    : Array.isArray(advisor.skills) ? advisor.skills : [];
  const partnerSummary = campaignData?.statement || {};
  const clientNeeds = Array.isArray(campaignData?.objectives)
    ? campaignData.objectives
    : [];
  const nameParts = advisorName.trim().split(/\s+/).filter(Boolean);
  const advisorPhoto = getOptionalText(partnerData.photo) || getOptionalText(advisor.logo);
  const previewCompany = {
    ...advisor,
    _id: advisor.id || advisor._id,
    isDefault: true,
    isAdvisor: true,
    companyName: advisorName,
    companyRoleUser: ["Advisor"],
    logo: advisorPhoto,
    country: advisorLocation,
  };
  const previewCreator = {
    ...advisor.creator,
    first_name: nameParts[0] || "Advisor",
    last_name: nameParts.slice(1).join(" "),
    email: partnerData.email || advisor.email || "",
    profilePic: { filename: advisorPhoto },
    isEmailVerified: uniqueUser?.isEmailVerified === true,
  };

  return (
    <>
      <StepIntroCard step={STEP.PREVIEW} />
      <div className="partner-advisor-preview">
        <section className="partner-advisor-preview-card">
          <div className="partner-preview-section-heading">
            <span>Your partner profile</span>
          </div>
          <div className="partner-advisor-preview-identity">
            <img
              className="partner-advisor-preview-photo"
              src={getAssetUrl(advisorPhoto) || "/assets/img/Frame1.svg"}
              alt="Partner profile"
              onError={event => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = "/assets/img/Frame1.svg";
              }}
            />
            <div>
              <h2>{advisorName}</h2>
              <p>kimbocorp partner</p>
            </div>
          </div>
          <div className="partner-advisor-preview-details">
            {releatedCompanyName && <div><span>Company name</span><strong>{releatedCompanyName}</strong></div>}
            {advisorEmail && <div><span>Email</span><strong>{advisorEmail}</strong></div>}
            {advisorLocation && <div><span>Country</span><strong>{advisorLocation}</strong></div>}
            {memberSinceLabel && <div><span>Member since</span><strong>{memberSinceLabel}</strong></div>}
            {advisorLinkedIn && <div><span>LinkedIn</span><strong>{advisorLinkedIn}</strong></div>}
          </div>
          <div className="partner-advisor-preview-about">
            <h3>About</h3>
            <p>{advisorAbout || "No description has been added yet."}</p>
          </div>
          {advisorSkills.length > 0 && (
            <div className="partner-advisor-preview-skills">
              <h3>Skills</h3>
              <div className="tags m-0">
                {advisorSkills.map(skill => <span key={skill}>{skill}</span>)}
              </div>
            </div>
          )}
          <div className="partner-advisor-preview-referrals">
            <h3>Who I refer</h3>
            <div className="partner-advisor-preview-summary">
              <span>Partner summary</span>
              {partnerSummary.title && <strong>{partnerSummary.title}</strong>}
              <p>{partnerSummary.body || "No partner summary has been added yet."}</p>
            </div>
            <div className="partner-advisor-preview-summary">
              <span>Client needs</span>
              {clientNeeds.length > 0 ? (
                <div className="tags m-0">
                  {clientNeeds.map(need => <span key={need}>{need}</span>)}
                </div>
              ) : (
                <p>No client needs selected yet.</p>
              )}
            </div>
          </div>
        </section>
        <aside className="partner-advisor-preview-side">
          <VerificationIcons
            company={previewCompany}
            isAdvisorUser={true}
            linkedinUrl={advisorLinkedIn}
            creator={previewCreator}
            className="partner-preview-verifications"
            showStatusLabels={true}
          />
          <p className="partner-verifications-note">
            Phone and LinkedIn are optional. ID checks happen before your first payout.
          </p>
        </aside>
      </div>
    </>
  );
}

function PartnerAppDownloadLinks() {
  return (
    <div className="partner-app-download">
      <div className="partner-app-download-title">
        <span className="partner-app-download-icon" aria-hidden="true">
          <img src="/assets/img/downloadIcon.svg" alt="" />
        </span>
        <strong>Download the kimbocorp app</strong>
      </div>
      <div className="partner-app-download-links">
        <a
          href="https://apps.apple.com/us/app/kimbocorp/id6737228466"
          target="_blank"
          rel="noreferrer"
          aria-label="Download kimbocorp from the App Store"
        >
          <img src="/assets/img/landingPage/applestore.svg" alt="Download on the App Store" />
        </a>
        <a
          href="https://play.google.com/store/apps/details?id=io.kimbocorp"
          target="_blank"
          rel="noreferrer"
          aria-label="Download kimbocorp from Google Play"
        >
          <img src="/assets/img/landingPage/playstore.svg" alt="Get it on Google Play" />
        </a>
      </div>
    </div>
  );
}

function ActivationSuccess() {
  return (
    <div className="partner-success" role="status">
      <span className="partner-success-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" focusable="false">
          <path d="M5 12.5 9.5 17 19 7.5" />
        </svg>
      </span>
      <h2>{PARTNER_SUCCESS.title}</h2>
      <p>{PARTNER_SUCCESS.message}</p>
      <div className="partner-success-next-steps">
        <h3>Next steps</h3>
        <ol>
          {PARTNER_SUCCESS.nextSteps.map(step => <li key={step}>{step}</li>)}
        </ol>
      </div>
      <div className="partner-success-actions">
        <PartnerAppDownloadLinks />
        <a
          className="partner-success-secondary"
          href={PARTNER_SUCCESS.briefingUrl}
          target="_blank"
          rel="noreferrer"
        >
          {PARTNER_SUCCESS.briefingAction} <span aria-hidden="true">→</span>
        </a>
      </div>
    </div>
  );
}

function PartnerActivationStatusCard({ issue, companyId }) {
  const navigate = useNavigate();
  const [supportChatLoading, setSupportChatLoading] = useState(false);
  const [supportChatError, setSupportChatError] = useState("");
  const alreadyActivated = issue === "already_activated";

  const openSupportChat = async event => {
    event.preventDefault();
    if (supportChatLoading) return;

    const resolvedCompanyId = companyId || localStorage.getItem("selectedCompany") || localStorage.getItem("companyId");
    if (!resolvedCompanyId) {
      setSupportChatError("We couldn’t open support chat. Please try again after signing in.");
      return;
    }

    setSupportChatLoading(true);
    setSupportChatError("");

    try {
      const response = await adminChatMessage({
        message: "<strong>Speak to admin:</strong> I need help with my account.",
        data: {},
        module: "",
        companyId: resolvedCompanyId,
      });
      const chatId = response?._id || response?.data?._id || response?.chat?._id || response?.chat;

      if (!chatId) {
        throw new Error("Support chat was not created");
      }

      navigate(`${RoutesPaths.MESSAGE_DETAIL_PUBLIC}?room=${encodeURIComponent(chatId)}`);
    } catch (error) {
      setSupportChatError("We couldn’t open support chat. Please try again.");
    } finally {
      setSupportChatLoading(false);
    }
  };

  return (
    <div className="partner-activation-status-card" role="status">
      <span className="partner-activation-status-icon" aria-hidden="true">
        {alreadyActivated ? "i" : "!"}
      </span>
      <h2>{alreadyActivated ? "Your partner account is already active" : "This activation link is no longer available"}</h2>
      <p>
        {alreadyActivated
          ? "This activation link has already been used. Sign in to the app to get your referral link and see your referrals."
          : "We couldn’t verify this partner activation link. It may have expired or already been used. Please request a new link if you need help."}
      </p>
      {alreadyActivated && (
        <div className="partner-status-actions">
          <PartnerAppDownloadLinks />
          <a
            className="partner-success-secondary"
            href={RoutesPaths.MESSAGE_DETAIL_PUBLIC}
            onClick={openSupportChat}
            aria-disabled={supportChatLoading}
          >
            {supportChatLoading ? "Opening support chat..." : "Need help? Contact us"}
          </a>
          {supportChatError && <small className="partner-support-chat-error" role="alert">{supportChatError}</small>}
        </div>
      )}
    </div>
  );
}

export default function ActivatePartner() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activationId = searchParams.get("id");
  const companies = useSelector(state => state.company?.company || []);
  const defaultCompany = useMemo(
    () => companies.find(company => company?.isDefault === true || company?.isDefault === "true") || null,
    [companies]
  );
  const defaultCompanyId = defaultCompany?.id || defaultCompany?._id;
  const profileIds = useSelector(state => state.peopleai?.profileIds || []);
  const [user, setUser] = useState(() => getUniqueUser());
  const [activationVerifying, setActivationVerifying] = useState(true);
  const [activationIssue, setActivationIssue] = useState("");
  const [companySaving, setCompanySaving] = useState(false);
  const [campaignLaunching, setCampaignLaunching] = useState(false);
  const [selectedStep, setSelectedStep] = useState(STEP.PROFILE);
  const [stepActionError, setStepActionError] = useState("");
  const [completed, setCompleted] = useState(false);
  const [goals, setGoals] = useState({
    goal: null,
    objectives: [],
    statement: { title: "", body: "" },
    audience: { role: [], location: [], industry: [] },
  });
  const [campaignData, setCampaignData] = useState(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      email: "",
      birthday: "",
      skills: [],
      releatedCompanyName: "",
      country: "",
      linkedin: "",
      about: "",
      photo: "",
      banner: "",
    },
  });

  const formValues = watch();
  const campaignAdvisor = useMemo(() => ({
    ...defaultCompany,
    companyName: formValues.name ?? defaultCompany?.companyName ?? "",
    about: formValues.about ?? defaultCompany?.about ?? "",
    skills: Array.isArray(formValues.skills) ? formValues.skills : (defaultCompany?.skills || []),
    releatedCompanyName: getOptionalText(formValues.releatedCompanyName) || getOptionalText(defaultCompany?.releatedCompanyName),
    country: formValues.country ?? defaultCompany?.country ?? "",
  }), [defaultCompany, formValues.about, formValues.country, formValues.name, formValues.releatedCompanyName, formValues.skills]);

  useEffect(() => {
    if (!defaultCompany) return;

    const userName = [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim();
    const defaultCountry = getOptionalText(defaultCompany.country?.name || defaultCompany.country);
    console.log("defaultCompany", defaultCompany);
    reset({
      name: userName || defaultCompany.companyName || "",
      email: user?.email || defaultCompany.email || "",
      birthday: getDateInputValue(defaultCompany.birthday),
      skills: Array.isArray(defaultCompany.skills) ? defaultCompany.skills : [],
      releatedCompanyName: getOptionalText(defaultCompany.releatedCompanyName),
      country: defaultCountry,
      linkedin: defaultCompany.linkedInUrl ? normalizeLinkedInUrl(defaultCompany.linkedInUrl) : "",
      about: defaultCompany.about || defaultCompany.aboutPrompt || "",
      photo: defaultCompany.logo || user?.profilePic?.filename || "",
      banner: defaultCompany.banner || "",
    });
  }, [defaultCompany, reset, user]);

  useEffect(() => {
    let isMounted = true;

    if (!activationId) {
      navigate(RoutesPaths.ERROR_PAGE, { replace: true });
      return undefined;
    }

    const checkActivation = async () => {
      try {
        const activationStatus = await dispatch(getPartnerActivationStatusAction({ id: activationId }));
        const isAlreadyActivated = activationStatus?.isActivated === true
          || activationStatus?.activationStatus === "already_activated";

        if (!isMounted) return;

        if (isAlreadyActivated) {
          setActivationIssue("already_activated");
          setActivationVerifying(false);
          return;
        }

        if (!activationStatus?.status || activationStatus?.isValid === false) {
          setActivationIssue("invalid");
          setActivationVerifying(false);
          return;
        }

        const verification = await dispatch(verifyPartnerActivationAction({ id: activationId }));
        if (!isMounted) return;

        if (verification?.status && verification?.isValid) {
          setUser(getUniqueUser());
          await dispatch(getAllCompany());
          setActivationVerifying(false);
        } else if (verification?.isActivated || verification?.activationStatus === "already_activated") {
          setActivationIssue("already_activated");
          setActivationVerifying(false);
        } else {
          setActivationIssue("invalid");
          setActivationVerifying(false);
        }
      } catch (error) {
        if (isMounted) {
          setActivationIssue("invalid");
          setActivationVerifying(false);
        }
      }
    };

    checkActivation();

    return () => {
      isMounted = false;
    };
  }, [activationId, dispatch, navigate]);

  useEffect(() => {
    setStepActionError("");
    window.scrollTo?.(0, 0);
  }, [selectedStep, completed]);

  const goToStep = step => setSelectedStep(Math.min(Math.max(step, 1), TOTAL_STEPS));

  const saveCompanyAndContinue = async data => {
    if (companySaving) return;

    if (!defaultCompanyId) {
      setStepActionError("No default advisor profile was found to update.");
      return;
    }

    setStepActionError("");
    setCompanySaving(true);

    try {
      const normalizedLinkedIn = normalizeLinkedInUrl(data.linkedin || "");
      const linkedInUsername = normalizedLinkedIn.replace(LINKEDIN_PREFIX, "").trim();
      const response = await dispatch(editCompany(defaultCompanyId, {
        companyName: data.name?.trim() || "",
        country: data.country || "",
        email: data.email || "",
        skills: Array.isArray(data.skills) ? data.skills : [],
        about: data.about?.trim() || "",
        ...(getOptionalText(data.releatedCompanyName) ? { releatedCompanyName: getOptionalText(data.releatedCompanyName) } : {}),
        linkedInUrl: linkedInUsername ? `${LINKEDIN_PREFIX}${linkedInUsername}` : "",
        logo: data.photo || "",
        banner: data.banner || "",
        birthday: data.birthday || "",
        id: defaultCompanyId,
      }));
      const isSuccessful = response?.status >= 200 && response?.status < 300;

      if (!isSuccessful) {
        setStepActionError(response?.data?.message || "Unable to update the advisor profile. Please try again.");
        return;
      }

      goToStep(STEP.GOALS);
    } catch (error) {
      setStepActionError("Unable to update the advisor profile. Please try again.");
    } finally {
      setCompanySaving(false);
    }
  };

  const launchCampaign = async () => {
    if (campaignLaunching) return;

    const campaign = campaignData?.campaign;
    if (!campaign || !defaultCompanyId) {
      setStepActionError("Complete the campaign setup before activating your partner account.");
      return;
    }

    setStepActionError("");
    setCampaignLaunching(true);

    try {
      const rawProfileIds = Array.isArray(profileIds) ? profileIds : String(profileIds || "").split(",");
      const profiles = Array.isArray(campaign.profiles) && campaign.profiles.length
        ? campaign.profiles
        : rawProfileIds.filter(Boolean).map(profile => ({ profile: String(profile).trim(), selected: true, verified: false }));
      const response = await dispatch(addCampaignAction({
        company: defaultCompanyId,
        location: campaign.location || [],
        industry: campaign.industry || [],
        targetting: campaign.targetting || [],
        onlyVerifiedUsers: campaign.onlyVerifiedUsers || false,
        profiles,
        name: campaign.name || campaignData.statement?.title || "Advisor campaign",
        description: campaign.description || campaignData.statement?.body || "",
        tags: campaign.tags || "",
        status: "launched",
        images: campaign.images || [],
      }));

      if (!response?.status) {
        setStepActionError(response?.message || "Unable to save your campaign. Please try again.");
        return;
      }

      const activationResponse = await dispatch(completePartnerActivationAction({
        id: activationId,
        isKimbocorpAdvisor: true,
      }));

      if (!activationResponse?.status) {
        setStepActionError("Your campaign was saved, but we couldn’t complete partner activation. Please try again.");
        return;
      }

      setCompleted(true);
    } catch (error) {
      setStepActionError("Unable to activate your partner account. Please try again.");
    } finally {
      setCampaignLaunching(false);
    }
  };

  const handleProfileInvalid = () => {
    setStepActionError("Please complete the required fields above before continuing.");
  };

  const handleBack = () => {
    if (selectedStep === STEP.PROFILE) {
      navigate(-1);
      return;
    }
    goToStep(selectedStep - 1);
  };

  const handleContinue = () => {
    if (selectedStep === STEP.PROFILE) {
      handleSubmit(
        saveCompanyAndContinue,
        handleProfileInvalid,
      )();
      return;
    }
    // Last step launches the campaign created from the selected goal and filters.
    if (selectedStep === STEP.PREVIEW) {
      launchCampaign();
      return;
    }
    goToStep(selectedStep + 1);
  };

  if (activationVerifying) {
    return (
      <div className="activate-page partner-theme">
        <main className="activate-main activate-content-full partner-activation-loader-page">
          <section className="partner-activation-loader" role="status" aria-live="polite">
            <div className="partner-activation-loader-mark" aria-hidden="true">
              <span className="partner-activation-loader-ring" />
              <img src="/assets/img/kc-small-logo-w.svg" alt="" />
            </div>
            <p>Verifying activation link...</p>
          </section>
        </main>
      </div>
    );
  }

  if (activationIssue) {
    return (
      <div className="activate-page partner-theme">
        <div className="partner-activation-status-shell">
          <header className="partner-status-brand-header">
            <img src="assets/img/inproved-logo.svg" alt="kimbocorp" />
          </header>
          <main className="activate-main activate-content-full partner-activation-status-main">
            <section className="activate-work">
              <PartnerActivationStatusCard issue={activationIssue} companyId={defaultCompanyId} />
            </section>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="activate-page partner-theme">
      <StepRail selectedStep={completed ? TOTAL_STEPS + 1 : selectedStep} />
      <main className="activate-main">
        <ActivationHeader user={user} />
        <div className="activate-content activate-content-full">
          <section className="activate-work">
            {completed ? (
              <ActivationSuccess />
            ) : (
              <>
                {selectedStep === STEP.PROFILE && (
                  <PartnerInfoStep
                    register={register}
                    errors={errors}
                    watch={watch}
                    setValue={setValue}
                  />
                )}
                {selectedStep === STEP.GOALS && (
                  <GoalsObjectivesStep
                    advisor={campaignAdvisor}
                    companyId={defaultCompanyId}
                    initialData={goals}
                    onBack={handleBack}
                    onContinue={preview => {
                      setGoals({
                        goal: preview.goal,
                        objectives: preview.objectives,
                        statement: preview.statement,
                        audience: preview.audience,
                      });
                      setCampaignData(preview);
                      setStepActionError("");
                      goToStep(STEP.PREVIEW);
                    }}
                  />
                )}
                {selectedStep === STEP.PREVIEW && (
                  <AdvisorPreviewStep
                    partnerData={formValues}
                    campaignData={campaignData}
                  />
                )}
                {(selectedStep === STEP.PROFILE || selectedStep === STEP.PREVIEW) && (
                  <div className={`activate-actions${stepActionError ? " has-error" : ""}${selectedStep === STEP.PREVIEW ? " partner-preview-actions" : ""}`}>
                    {stepActionError && (
                      <p className="activate-actions-error" role="alert">
                        {stepActionError}
                      </p>
                    )}
                    {selectedStep === STEP.PREVIEW && (
                      <p className="partner-activation-terms">
                        By activating, you agree to the{" "}
                        <a href={termsOfAdvisoryPlan} target="_blank" rel="noreferrer">
                          Advisor Terms and Conditions.
                        </a>
                      </p>
                    )}
                    <button type="button" className="activate-back" onClick={handleBack}>
                      Back
                    </button>
                    <button type="button" className="activate-continue" onClick={handleContinue} disabled={companySaving || campaignLaunching}>
                      {selectedStep === STEP.PREVIEW
                        ? campaignLaunching ? "Activating..." : "Activate my partner account"
                        : companySaving ? "Saving..." : "Continue"}
                    </button>
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
